import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { produce } from 'immer'

import { ApprovalProcessReviewerRepository } from './domain'

import {
  AdminApprovalProcessListCriteria,
  AdminApprovalProcessListItem,
  ApprovalProcessReviewerView,
  ApprovalRequest,
  ApproveModificationRequestCommand,
  ApproveRequestCommand,
} from '../../contracts/service-offer'
import { AxiosErrorHandler, SupernovaPagedBody } from '../../foundation'

export class AdminApiApprovalProcess implements ApprovalProcessReviewerRepository {
  private errorHandler: AxiosErrorHandler

  constructor(private axios: AxiosInstance) {
    this.errorHandler = new AxiosErrorHandler()
  }

  getById = async (id: number): Promise<ApprovalProcessReviewerView> => {
    const config: AxiosRequestConfig<{ id: number }> = {
      url: '/service-offer/query/admin/approval-process/process',
      method: 'post',
      data: { id },
    }

    try {
      const { data } = await this.axios.request<ApprovalProcessReviewerView>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modify = async (command: ApprovalRequest & { approvalProcessId: number; hospitalId: number }): Promise<void> => {
    const config: AxiosRequestConfig<ApprovalRequest & { approvalProcessId: number; hospitalId: number }> = {
      url: '/service-offer/command/admin/modify-approval-process/execute',
      method: 'post',
      data: command,
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getList = async (
    criteria: AdminApprovalProcessListCriteria,
  ): Promise<SupernovaPagedBody<AdminApprovalProcessListItem[]>> => {
    const config: AxiosRequestConfig<AdminApprovalProcessListCriteria> = {
      url: '/service-offer/query/admin/approval-process-list/process',
      method: 'post',
      data: produce(criteria, (draft: AdminApprovalProcessListCriteria) => {
        if (draft.filter?.status && !Array.isArray(draft.filter?.status)) {
          draft.filter.status = [draft.filter?.status]
        }
        return draft
      }),
    }

    try {
      const { data } = await this.axios.request<SupernovaPagedBody<AdminApprovalProcessListItem[]>>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  postMemo = async (approvalProcessId: number, memo: string): Promise<void> => {
    const config: AxiosRequestConfig<{ approvalProcessId: number; memo: string }> = {
      url: '/service-offer/command/admin/post-memo/execute',
      method: 'post',
      data: { approvalProcessId, memo },
    }

    try {
      const { data } = await this.axios.request<void>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  approveCreation = async (
    id: number,
    { reason, adDeductionPoint, memo }: { reason: string; adDeductionPoint: number; memo?: string },
  ): Promise<void> => {
    const config: AxiosRequestConfig<ApproveRequestCommand> = {
      url: '/service-offer/command/admin/approve-creation/execute',
      method: 'post',
      data: {
        approvalProcessId: id,
        reasonForProcessStatusChange: reason,
        adDeductionPoint,
        memo: memo ?? null,
      },
    }

    try {
      const { data } = await this.axios.request<void>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  rejectCreation = async (id: number, reason: string): Promise<void> => {
    const config: AxiosRequestConfig<{
      approvalProcessId: number
      reasonForProcessStatusChange: string
    }> = {
      url: '/service-offer/command/admin/reject-creation/execute',
      method: 'post',
      data: {
        approvalProcessId: id,
        reasonForProcessStatusChange: reason,
      },
    }

    try {
      const { data } = await this.axios.request<void>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  pendingCreation = async (id: number, reason: string): Promise<void> => {
    const config: AxiosRequestConfig<{
      approvalProcessId: number
      reasonForProcessStatusChange: string
    }> = {
      url: '/service-offer/command/admin/pending-creation/execute',
      method: 'post',
      data: {
        approvalProcessId: id,
        reasonForProcessStatusChange: reason,
      },
    }

    try {
      const { data } = await this.axios.request<void>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  approveModification = async (
    id: number,
    {
      reason,
      adDeductionPoint,
      memo,
      serviceOfferId,
    }: { reason: string; adDeductionPoint: number; memo?: string; serviceOfferId: number },
  ): Promise<void> => {
    const config: AxiosRequestConfig<ApproveModificationRequestCommand> = {
      url: '/service-offer/command/admin/approve-modification/execute',
      method: 'post',
      data: {
        approvalProcessId: id,
        reasonForProcessStatusChange: reason,
        adDeductionPoint,
        memo: memo ?? null,
        serviceOfferId,
      },
    }

    try {
      const { data } = await this.axios.request<void>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  rejectModification = async (id: number, reason: string): Promise<void> => {
    const config: AxiosRequestConfig<{
      approvalProcessId: number
      reasonForProcessStatusChange: string
    }> = {
      url: '/service-offer/command/admin/reject-modification/execute',
      method: 'post',
      data: {
        approvalProcessId: id,
        reasonForProcessStatusChange: reason,
      },
    }

    try {
      const { data } = await this.axios.request<void>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  pendingModification = async (id: number, reason: string): Promise<void> => {
    const config: AxiosRequestConfig<{
      approvalProcessId: number
      reasonForProcessStatusChange: string
    }> = {
      url: '/service-offer/command/admin/pending-modification/execute',
      method: 'post',
      data: {
        approvalProcessId: id,
        reasonForProcessStatusChange: reason,
      },
    }

    try {
      const { data } = await this.axios.request<void>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }
}
